/**
 * Favoris du compte (27/10/2026) : une seule source, `/_api/auth/favoris`, lue par le menu « Mon espace »
 * (pages serveur par navEspace.ts, application par EspaceMenu) et par le tableau de bord.
 * PUT remplace la liste entière : le serveur refuse une page hors catalogue.
 */

export type Favori = { path: string; href: string; label: string; desc: string; icon: string; tier: string | null; isStatic: boolean };
export type GroupeChoix = { label: string; pages: { path: string; label: string }[] };
export type Favoris = { favoris: Favori[]; max: number; choix: GroupeChoix[] };

export const FAVORIS_URL = "/_api/auth/favoris";

export async function chargerFavoris(fetchFn: typeof fetch = fetch): Promise<Favoris | null> {
  const r = await fetchFn(FAVORIS_URL, { credentials: "same-origin" });
  if (!r.ok) return null;
  const data = (await r.json().catch(() => null)) as Favoris | null;
  // Réponse d'une autre forme (ancien serveur pendant un déploiement, page d'erreur) : aucun favori affiché.
  return data && Array.isArray(data.favoris) && Array.isArray(data.choix) && typeof data.max === "number" ? data : null;
}

export async function enregistrerFavoris(paths: string[], fetchFn: typeof fetch = fetch): Promise<Favoris> {
  const r = await fetchFn(FAVORIS_URL, {
    method: "PUT",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ paths }),
  });
  const data = await r.json().catch(() => null);
  if (!r.ok) throw new Error((data as { error?: string } | null)?.error ?? "Favoris non enregistrés");
  return data as Favoris;
}

/** Adresse de page comparable au catalogue : sans query ni ancre, avec le slash final. */
export function pageCourante(pathname: string): string {
  return pathname.endsWith("/") ? pathname : `${pathname}/`;
}

export function estEpinglable(f: Favoris, path: string): boolean {
  return f.choix.some((g) => g.pages.some((p) => p.path === path));
}

/**
 * Ajoute ou retire ; null si la liste est pleine. Depuis le menu (`enTete`), la page affichée passe en
 * premier : c'est celle à laquelle on pense, et elle reste visible dans les premiers liens du menu.
 */
export function basculer(paths: string[], path: string, max: number, enTete = false): string[] | null {
  if (paths.includes(path)) return paths.filter((p) => p !== path);
  if (paths.length >= max) return null;
  return enTete ? [path, ...paths] : [...paths, path];
}

/** Annule un retrait : la page revient à son rang d'avant. */
export function reinserer(paths: string[], path: string, index: number): string[] {
  if (paths.includes(path)) return paths;
  const copie = [...paths];
  copie.splice(Math.min(Math.max(index, 0), copie.length), 0, path);
  return copie;
}

/** Échange avec le voisin (−1 vers le haut, +1 vers le bas) ; liste inchangée au bord. */
export function deplacer(paths: string[], index: number, sens: -1 | 1): string[] {
  const cible = index + sens;
  if (index < 0 || index >= paths.length || cible < 0 || cible >= paths.length) return paths;
  const copie = [...paths];
  [copie[index], copie[cible]] = [copie[cible], copie[index]];
  return copie;
}

/** Favoris affichés dans le menu ; au-delà, « Gérer mes favoris » donne le nombre et mène à l'éditeur. */
export const FAVORIS_MENU = 6;
export const GERER_FAVORIS_URL = "/home/?favoris=1";

/** Liste pleine : un seul message, qui nomme le lien affiché dans le menu (libelleGerer). */
export function messagePlein(max: number): string {
  return `${max} favoris au plus : retirez-en un dans « Gérer mes favoris ».`;
}

/** Libellé du lien vers l'éditeur, toujours le même ; le total dit quand des favoris sont masqués. */
export function libelleGerer(total: number): string {
  return total > FAVORIS_MENU ? `Gérer mes favoris (${total})` : "Gérer mes favoris";
}

/**
 * Menu « Mon espace » des pages serveur (MagHeaderRenderer) : favoris chargés à la première ouverture,
 * bouton « Ajouter cette page » ou « Retirer » selon la page affichée (`data-epinglee`, icône signet
 * plus ou moins en CSS), annonce polie du résultat ; liste pleine : le bouton reste et dit pourquoi. Le bouton n'est jamais désactivé :
 * le focus clavier reste dessus, un drapeau bloque le double clic.
 */
export function initFavorisMenu(doc: Document, fetchFn: typeof fetch = fetch): void {
  const box = doc.querySelector<HTMLElement>("[data-km-me]");
  const btn = box?.querySelector<HTMLButtonElement>("[data-km-me-btn]");
  const groupe = box?.querySelector<HTMLElement>("[data-km-favoris]");
  const liste = groupe?.querySelector<HTMLElement>("[data-km-favoris-liste]");
  const bascule = groupe?.querySelector<HTMLButtonElement>("[data-km-favori-page]");
  const texte = bascule?.querySelector<HTMLElement>("[data-km-favori-texte]");
  const annonce = groupe?.querySelector<HTMLElement>("[data-km-favori-annonce]");
  const gerer = groupe?.querySelector<HTMLAnchorElement>("[data-km-favoris-gerer]");
  const icone = groupe?.querySelector<HTMLTemplateElement>("[data-km-favori-icone]");
  if (!btn || !groupe || !liste || !bascule || !texte) return;
  const page = pageCourante(doc.location?.pathname ?? "/");
  let etat: Favoris | null = null;
  let charge = false;
  let enCours = false;

  const afficher = (f: Favoris) => {
    etat = f;
    liste.replaceChildren(...f.favoris.slice(0, FAVORIS_MENU).map((fav) => {
      const a = doc.createElement("a");
      a.href = fav.href;
      if (fav.path === page) a.setAttribute("aria-current", "page");
      const svg = icone?.content.firstElementChild;
      if (svg) a.append(svg.cloneNode(true));
      a.append(fav.label);
      return a;
    }));
    const epinglee = f.favoris.some((fav) => fav.path === page);
    const possible = estEpinglable(f, page);
    bascule.hidden = !possible;
    bascule.toggleAttribute("data-epinglee", epinglee);
    texte.textContent = epinglee ? "Retirer cette page des favoris" : "Ajouter cette page aux favoris";
    if (gerer) {
      gerer.hidden = f.favoris.length === 0;
      gerer.textContent = libelleGerer(f.favoris.length);
    }
    groupe.hidden = f.favoris.length === 0 && !possible;
  };

  btn.addEventListener("click", () => {
    if (charge) return;
    charge = true;
    void chargerFavoris(fetchFn).then((f) => f && afficher(f)).catch(() => {});
  });
  bascule.addEventListener("click", () => {
    if (!etat || enCours) return;
    const ajout = !etat.favoris.some((f) => f.path === page);
    const paths = basculer(etat.favoris.map((f) => f.path), page, etat.max, true);
    if (!paths) {
      texte.textContent = messagePlein(etat.max);
      if (annonce) annonce.textContent = messagePlein(etat.max);
      return;
    }
    enCours = true;
    bascule.setAttribute("aria-busy", "true");
    void enregistrerFavoris(paths, fetchFn)
      .then((f) => {
        afficher(f);
        if (annonce) annonce.textContent = ajout ? "Page ajoutée aux favoris." : "Page retirée des favoris.";
      })
      .catch(() => { if (annonce) annonce.textContent = "Favoris non enregistrés : vérifiez votre connexion et réessayez."; })
      .finally(() => { enCours = false; bascule.removeAttribute("aria-busy"); });
  });
}
